(ns nl.mediquest.questionnaire-frontend.component.surveyjs.model
  "For adding localization the Survey.Model need to be passed to the Survey React
  component. Here we ensure the Model is properly prepared before
  instantiating."
  (:require
   ["survey-core/themes" :refer [SharpLightPanelless]]
   [nl.mediquest.questionnaire-frontend.component.i18n :as i18n]
   [nl.mediquest.questionnaire-frontend.component.surveyjs.renderers.rating-widget]
   [nl.mediquest.questionnaire-frontend.config :as config]
   [re-frame.core :as re-frame]
   [survey-core
    :refer [Model Serializer surveyLocalization]]))

(defn- set-default-locale []
  (set! (.-defaultLocale surveyLocalization) "nl"))

(defn add-info-property
  "Info icon to display more information available in `:surveyjs/infoDescription`
  and `surveyjs/infoKey` keys on question"
  []
  (.. Serializer (addProperty "question" "infoTitle"))
  (.. Serializer (addProperty "question" "infoDescription")))

(defn add-empty-survey-text-for-missing-unsubscribe-page []
  (when @(re-frame/subscribe [:questionnaire/unsubscribe?])
    (set! (.. surveyLocalization -locales -nl -emptySurvey)
          "Er is geen uitschrijfpagina voor deze vragenlijst.")))

(defn- apply-default-theme []
  (doto (.-cssVariables SharpLightPanelless)
    (js/goog.object.set "--sjs-primary-backcolor" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-backcolor-dark" "var(--color-primary-600)")
    (js/goog.object.set "--sjs-primary-backcolor-light" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-forecolor" "#ffffff")

    ;; General colors
    (js/goog.object.set "--sjs-forecolor" "#403e3f")
    (js/goog.object.set "--sjs-header-backcolor" "var(--color-primary)")
    (js/goog.object.set "--sjs-header-backcolor-light" "#ffffff")
    (js/goog.object.set "--sjs-body-container-background-color" "#ffffff")

    ;; Font settings
    (js/goog.object.set "--sjs-font-family" "Poppins, sans-serif")
    (js/goog.object.set "--sjs-font-size" "14px")

    ;; Border and input styling
    (js/goog.object.set "--sjs-border-default" "#e7e7e7")
    (js/goog.object.set "--sjs-border-light" "#f0f0f0")
    (js/goog.object.set "--sjs-corner-radius" "4px")

    ;; Button styling
    (js/goog.object.set "--sjs-primary-button-background" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-button-background-hover" "var(--color-primary-600)")
    (js/goog.object.set "--sjs-primary-button-forecolor" "#ffffff"))
  SharpLightPanelless)

(defn- apply-vitaal-bedrijf-theme []
  (doto (.-cssVariables SharpLightPanelless)
    ;; Primary colors - orange theme for vitaliteitsscan, use CSS variables
    (js/goog.object.set "--sjs-primary-backcolor" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-backcolor-dark" "var(--color-primary-600)")
    (js/goog.object.set "--sjs-primary-backcolor-light" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-forecolor" "#ffffff")

    ;; General colors
    (js/goog.object.set "--sjs-forecolor" "#000748")
    (js/goog.object.set "--sjs-header-backcolor" "var(--color-primary)")
    (js/goog.object.set "--sjs-header-backcolor-light" "#ffffff")
    (js/goog.object.set "--sjs-body-container-background-color" "#ffffff")

    ;; Font settings
    (js/goog.object.set "--sjs-font-family" "Poppins, sans-serif")
    (js/goog.object.set "--sjs-font-size" "14px")

    ;; Border and input styling
    (js/goog.object.set "--sjs-border-default" "#e7e7e7")
    (js/goog.object.set "--sjs-border-light" "#f0f0f0")
    (js/goog.object.set "--sjs-corner-radius" "4px")

    ;; Button styling
    (js/goog.object.set "--sjs-primary-button-background" "var(--color-primary)")
    (js/goog.object.set "--sjs-primary-button-background-hover" "var(--color-primary-600)")
    (js/goog.object.set "--sjs-primary-button-forecolor" "#ffffff"))
  SharpLightPanelless)

(def save-draft-button
  (atom nil))

(defn make-draft-button-visible [visible?]
  (when @save-draft-button
    (set! (.-visible @save-draft-button) visible?)))

(defn- save-draft [survey-model]
  (make-draft-button-visible false)
  (re-frame/dispatch [:nl.mediquest.questionnaire-frontend.events/save-draft
                      {:answers
                       (js->clj (.-data survey-model) :keywordize-keys true)
                       :page-no (.-currentPageNo survey-model)}]))

(defn- add-save-draft-button
  [survey-model]
  (reset! save-draft-button
          (.addNavigationItem survey-model
                              (clj->js
                               {:id "sv-nav-save"
                                :title (i18n/text :survey/save-draft)
                                :visible false
                                :action
                                #(save-draft survey-model)
                                :visibleIndex 25}))))

(defn make-survey-model
  [surveyjs-config {:keys [answers page-no] :as draft-response}]
  (set-default-locale)
  (add-info-property)
  (add-empty-survey-text-for-missing-unsubscribe-page)
  (let [theme (if config/vitaliteitsscan?
                (apply-vitaal-bedrijf-theme)
                (apply-default-theme))
        model (Model. (clj->js surveyjs-config))]
    (when draft-response
      (set! (.-data model) (clj->js answers))
      (set! (.-currentPageNo model) page-no))
    (when (:allow-save-draft? surveyjs-config)
      (add-save-draft-button model))
    (doto model
      (.applyTheme theme))))
